import { FlexCol, FlexRow } from '@/components/common/Flex';
import { MotionBox } from '@/components/common/Motion';
import { groupHoverStyleProps } from '@/components/common/styleHelpers';
import { Box, FlexProps, Skeleton, Stack, Text } from '@chakra-ui/react';
import { toPx } from '@common/util/styles';
import NextLink from 'next/link';
import { NextRouter, useRouter } from 'next/router';
import { useState } from 'react';

export type SidebarNavPageItem =
    | {
          id?: string;
          href: string;
          text: string;
          isHidden?: boolean;
          isSelected?: boolean;
          isAdmin?: boolean;
          target?: string;
          indicatorColor?: string;
      }
    | {
          id?: string;
          onClick: ({ router }: { router: NextRouter }) => void;
          text: string;
          isHidden?: boolean;
          isSelected?: boolean;
          isAdmin?: boolean;
      };

export interface SidebarNavProps extends FlexProps {
    items: SidebarNavPageItem[];
    isLoading?: boolean;
    isSettings?: boolean;
    showMine?: boolean;
}

const LINK_HEIGHT = 45;
const TEXT_HEIGHT = 37;

const SidebarNav = ({
    items,
    isLoading,
    isSettings,
    showMine,
    ...props
}: SidebarNavProps) => {
    const [hoveredItem, setHoveredItem] = useState<SidebarNavPageItem | null>(
        null
    );
    const router = useRouter();
    if (isLoading) return <LoadingRows />;

    return (
        <FlexRow gap={4} position="relative" ml={-4} {...props}>
            <FlexRow>
                <ActiveItemIndicator hoveredItem={hoveredItem} items={items} />
            </FlexRow>
            <FlexCol>
                {items.map((item) => {
                    const itemProps = {
                        borderRadius: 'md',
                        w: '100%',
                        ml: -3,
                        role: 'group',
                        align: 'center',
                        py: 1,
                        gap: 2,
                        h: LINK_HEIGHT,
                        onMouseEnter: () => setHoveredItem(item),
                        onMouseLeave: () => setHoveredItem(null),
                    };

                    let textColor: string;

                    if (item.isSelected || item === hoveredItem) {
                        if (isSettings) {
                            textColor = 'gray.800';
                        } else {
                            textColor = showMine ? 'white' : 'gray.800';
                        }
                    } else {
                        textColor = 'gray.400';
                    }

                    const label = (
                        <Text
                            display="block"
                            fontSize="md"
                            px={3}
                            py={2}
                            fontWeight="medium"
                            color={textColor}
                            whiteSpace="nowrap"
                            h={TEXT_HEIGHT}
                            {...groupHoverStyleProps({ color: textColor })}
                            fontStyle={item.isAdmin ? 'italic' : 'normal'}
                        >
                            {item.text}
                        </Text>
                    );

                    // Weird stutter required to make typescript happy
                    if ('href' in item) {
                        return (
                            <FlexRow
                                id={item.id}
                                key={item.text}
                                {...itemProps}
                                href={item.href}
                                as={NextLink}
                                target={item.target}
                                align="center"
                            >
                                {label}
                            </FlexRow>
                        );
                    } else {
                        return (
                            <FlexRow
                                id={item.id}
                                key={item.text}
                                {...itemProps}
                                onClick={() => item.onClick({ router })}
                            >
                                {label}
                            </FlexRow>
                        );
                    }
                })}
            </FlexCol>
        </FlexRow>
    );
};

const ActiveItemIndicator = ({
    hoveredItem,
    items,
}: {
    hoveredItem: SidebarNavPageItem | null;
    items: SidebarNavPageItem[];
}) => {
    const selectedItem = items.find((item) => item.isSelected);

    if (!selectedItem) return;

    const activeItem = hoveredItem || selectedItem;
    const activeItemIndex = items.findIndex(
        (item) => item.text === activeItem.text
    );

    const indicatorOffset = activeItemIndex * LINK_HEIGHT + TEXT_HEIGHT / 2;

    return (
        <MotionBox
            position="absolute"
            initial={{ top: toPx(indicatorOffset) }}
            animate={{ top: toPx(indicatorOffset) }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            transition={{
                type: 'spring',
                stiffness: 500,
                damping: 60,
                mass: 1,
                delay: 0.05,
            }}
        >
            <Box
                w={2}
                h={2}
                mr={-2}
                bg={
                    ('indicatorColor' in activeItem &&
                        activeItem.indicatorColor) ||
                    'black'
                }
                borderRadius="full"
            />
        </MotionBox>
    );
};

const LoadingRows = () => (
    <Stack spacing={5}>
        <Skeleton w={100} h={5} />
        <Skeleton w={100} h={5} />
        <Skeleton w={100} h={5} />
        <Skeleton w={100} h={5} />
    </Stack>
);

export default SidebarNav;
